import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Modal } from 'react-bootstrap';
import NumberFormat from 'react-number-format';
import { withStyles, makeStyles, Card, CardContent, Button, IconButton, Tooltip } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MonetizationOnOutlinedIcon from '@material-ui/icons/MonetizationOnOutlined';
import InfoIcon from '@material-ui/icons/Info';
import SvgIcon from '../../../Helpers/Svg/SvgIcon';
import './QuoteCard.scss';

const TooltipColor = withStyles((theme) => ({
  tooltip: {
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[1],
    fontSize: 11,
  },
}))(Tooltip);

const useStyles = makeStyles((theme) => ({
  primaryColor: {
    color: theme.palette.primary.main,
  },
  convertIcon:{
    border:`1px solid ${theme.palette.primary.main}`,
    borderRadius:'50%',
    padding:'2px',
  },
  primaryBackground: {
    backgroundColor: theme.palette.primary.main,
  },
  carrierLogo: {
    maxWidth: "154px",
    maxHeight: "51px",
  },
  rateMargin: {
    marginRight: '40px',
  },
}));

export default function QuoteCard(props) {
  const classes = useStyles();
  const [isProductDetailsDialogOpen, setIsProductDetailsDialogOpen] = useState(false);

  let appointmentIcon;
  let canSubmitProduct;

  const getBadge = () => {
    // if this product has more than 1 badge, display the badge that corresponds to the sorting if available
    const bestCount = [props.isBestPrice, props.isFastest, props.isMostOptions].filter((f) => f === true).length;

    if (bestCount > 1) {

      if (props.isBestPrice && props.sortBy === 'price') {
        return (
          <Fragment>
            Best <br /> Price
          </Fragment>
        );
      }

      if (props.isFastest && props.sortBy === 'speed') {
        return (
          <Fragment>
            <br />
                        Fastest
          </Fragment>
        );
      }

      if (props.isMostOptions && props.sortBy === 'options') {
        return (
          <Fragment>
            Most <br />
                        Options
          </Fragment>
        );
      }
    }

    // now either it only has 1 badge, or it doesn't have the badge being sorted by, so pick one

    if (props.isBestPrice) {
      return (
        <Fragment>
          Best <br />
                    Price
        </Fragment>
      );
    }

    if (props.isFastest) {
      return (
        <Fragment>
          <br />
                    Fastest
        </Fragment>
      );
    }

    return (
      <Fragment>
        Most <br />
                Options
      </Fragment>
    );
  };

  const productDetailsButton = (
    <Row id={`product-detail-btn-row-${props.id}`} name="product-detail-btn-row">
      <Col className="mb-2" id={`product-detail-btn-col-${props.id}`} name="product-detail-btn-col">
        <Button id={`product-detail-btn-${props.id}`} name="product-detail-btn" className={`product-details-button ${classes.primaryColor}`} onClick={() => setIsProductDetailsDialogOpen(true)}>PRODUCT DETAILS</Button>
      </Col>
    </Row>
  );

  return (
    <div id={`quote-product-${props.id}`} name="quote-product">
      <Card variant="outlined" className="highlight-quote-card-term pb-0" id={`quote-product-info-${props.id}`} name="quote-product-info">
        <p id={`quote-product-rating-${props.id}`} name="quote-product-rating" className={`rating-text ml-3 mb-0 pt-2 ${classes.primaryColor}`}>{props.rating} Rating</p>
        {(props.isBestPrice || props.isFastest || props.isMostOptions) && <div id={`quote-product-badges-${props.id}`} name="quote-product-badges" className={`ribbon ${classes.primaryBackground}`}>{getBadge()}</div>}

        {props.isHighlighted && (
          <CardContent className="card-content mt-0" id={`quote-product-content-${props.id}`} name="quote-product-content">
            <p className="d-flex justify-content-center" id={`quote-product-logo-${props.id}`} name="quote-product-logo">
              <img className={`${classes.carrierLogo} carrier-logo-lg mb-1 ml-1 d-flex justify-content-center`} src={props.logoUrl} id={`product-logo-img-${props.id}`} name="product-logo-img" alt={`${props.carrierName} Logo`} />
            </p>
            <p className="product d-flex justify-content-center" id={`quote-product-name-${props.id}`} name="quote-product-name" ><strong>{props.productName}</strong></p>
            <Stats
              isAcceleratedUnderwriting={props.isAcceleratedUnderwriting}
              underwritingType={props.underwritingType}
              additionalOptionsStars={props.additionalOptionsStars}
              underwritingTimeTypical={props.underwritingTimeTypical}
              requiresMedicalExam={props.requiresMedicalExam}
              waverOfPremium={props.waverOfPremium}
              accidentalDeathBenefit={props.accidentalDeathBenefit}
              childRider={props.childRider}
              rate={props.premiumAs === 'monthly' ? props.monthlyRate : props.annualRate}
              monthlyRate={props.monthlyRate}
              annualRate={props.annualRate}
              isMonthlyRate={props.premiumAs === 'monthly' ? true : false}
              isHighlighted={props.isHighlighted}
              riderCost={props.premiumAs === 'monthly' ? props.riderCostMonthly : props.riderCost}
              id={props.id} 
            />
            <Button id={`select-btn-${props.id}`} name="select-btn" className="w-100 apply" color="primary" variant="contained" onClick={props.onClick} disabled={props.isProspectMode}>
              Select &nbsp;{appointmentIcon}
            </Button>
            <span className="d-flex justify-content-center mt-2" id={`quote-product-btn-content-${props.id}`} name="quote-product-btn-content">
              {productDetailsButton}
            </span>
          </CardContent>
        )}

        {!props.isHighlighted && (
          <CardContent className="card-content pt-0" id={`product-unhighlighted-content-${props.id}`} name="product-unhighlighted-content">
            <Row className="align-items-center"  id={`product-info-row-${props.id}`} name="product-info-row">
              <Col md={3} sm={3} id={`product-info-logo-col-${props.id}`} name="product-info-logo-col">
                <p className="d-flex justify-content-center" id={`product-unhighlighted-logo-${props.id}`} name="product-unhighlighted-logo"  >
                  <img className={`${classes.carrierLogo} carrier-logo-lg`} src={props.logoUrl} id={`product-unhighlighted-logo-img-${props.id}`} name="product-unhighlighted-logo-img"  alt={`${props.carrierName} Logo`} />
                </p>
              </Col>
              <Col md={9} sm={9} id={`product-info-content-col-${props.id}`} name="product-info-content-col">
                <Row className="d-flex justify-content-space-between" id={`product-name-content-row-${props.id}`} name="product-name-content-row">
                  <Col className="pl-0 alignEnd text-left" id={`product-name-content-col-${props.id}`} name="product-name-content-col">
                    <p className="product alignEnd mb-3" id={`quote-product-name-${props.id}`} name="quote-product-name"><strong>{props.productName}</strong></p>
                  </Col>
                  <Col className={`text-right ${classes.rateMargin}`} id={`product-amount-content-col-${props.id}`} amount="product-amount-content-col">
                    <div className={`${classes.primaryColor}`}>
                    <NumberFormat
                      id={`quote-amount-rate-${props.id}`}
                      name="quote-amount-rate"
                      className={`quote-amount-dollars ${classes.primaryColor}`}
                      value={props.premiumAs === 'monthly' ? props.monthlyRate : props.annualRate}
                      displayType={'text'}
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                    <span className="quote-amount-mo" id={`product-premium-type-${props.id}`} name="product-monthly-premium">{props.premiumAs && props.premiumAs === 'monthly' ? ' /mo' : ' /year'}</span>
                    
                    </div>
                    <div>
                    <NumberFormat
                      className={`quote-sub-amount-dollars-highlighted`}
                      value={props.premiumAs === 'monthly' ? props.annualRate : props.monthlyRate}
                      displayType={'text'}
                      id={`quote-sub-amount-rate-${props.id}`}
                      name='quote-sub-amount-rate'
                      thousandSeparator={true}
                      decimalScale={2}
                      fixedDecimalScale={true}
                    />
                    <span className={`quote-sub-amount-mo`} id={`product-sub-premium-type-${props.id}`} name='product-sub-premium-type'>{props.premiumAs && props.premiumAs === 'monthly' ? ' /year' : ' /mo'}</span>

                    </div>
                  </Col>
                </Row>
                <Row id={`product-unhighlighted-stats-${props.id}`} name="product-unhighlighted-stats">
                  <Stats
                    isAcceleratedUnderwriting={props.isAcceleratedUnderwriting}
                    underwritingType={props.underwritingType}
                    additionalOptionsStars={props.additionalOptionsStars}
                    underwritingTimeTypical={props.underwritingTimeTypical}
                    requiresMedicalExam={props.requiresMedicalExam}
                    rating={props.rating}
                    waverOfPremium={props.waverOfPremium}
                    accidentalDeathBenefit={props.accidentalDeathBenefit}
                    childRider={props.childRider}
                    rate={props.premiumAs === 'monthly' ? props.monthlyRate : props.annualRate}
                    monthlyRate={props.monthlyRate}
                    annualRate={props.annualRate}
                    isMonthlyRate={props.premiumAs === 'monthly' ? true : false}
                    isHighlighted={props.isHighlighted}
                    riderCost={props.premiumAs === 'monthly' ? props.riderCostMonthly : props.riderCost}
                    id={props.id} 
                  />
                </Row>
              </Col>
            </Row>
            {!props.isPrintCards &&
              <Row className="d-flex justify-content-space-between" id={`product-btn-content-${props.id}`} name="product-btn-content">
                <Col className="text-left" id={`product-details-btn-content-${props.id}`} name="product-details-btn-content">
                  {productDetailsButton}
                </Col>
              <Col className="text-right" id={`product-select-btn-content-${props.id}`} name="product-select-btn-content">
                  <Button id={`select-btn-${props.id}`} name="select-btn" className="apply px-5" color="primary" variant="contained" onClick={props.onClick} disabled={props.isProspectMode}>
                    Select &nbsp;{appointmentIcon}
                  </Button>
                </Col>
              </Row>
            }
          </CardContent>
        )}
      </Card>

      <ProductDetailsDialog
        isOpen={isProductDetailsDialogOpen}
        productDetails={props.productDetails}
        quoteCardProps={props}
        onClose={() => setIsProductDetailsDialogOpen(false)}
        isProspectMode={props.isProspectMode}
        appointmentIcon={appointmentIcon}
        canSubmitProduct={canSubmitProduct}
        id={props.id}
      />
    </div>
  );
}

const ProductDetailsRatingCard = (props) => {
  const classes = useStyles();
  return (
    <Card className="rating-card" id={`product-details-rating-card-${props.id}`} name="product-details-rating-card">
      <div className="rating-card-head" id={`product-details-rating-card-logo-${props.id}`} name="product-details-rating-card-logo">
        <img src={props.logo} alt={props.rater} className="img-fluid" id={`product-details-rating-card-logo-img-${props.id}`} name="product-details-rating-card-logo-img"/>
      </div>
      <p className="rater" id={`product-details-rater-${props.id}`} name="product-details-rater">{props.rater}</p>
      <p className={`rating ${classes.primaryColor}`} id={`product-details-rating-${props.id}`} name="product-details-rating">{props.rating}</p>
    </Card>
  );
};

const ProductDetailsDialog = (props) => {
  const classes = useStyles();
  const hasRating =
    props.quoteCardProps.ratingAMBest ||
    props.quoteCardProps.ratingSP ||
    props.quoteCardProps.ratingMoodys ||
    props.quoteCardProps.ratingFitch ||
    props.quoteCardProps.ratingComdex;

  const handleSelectedProduct = async() => {
    await props.quoteCardProps.onClick();
    props.onClose();
  }

  return (
    <Modal size='lg' className="product-details-dialog-term" show={props.isOpen} onHide={props.onClose} scroll="body" fullWidth={true} maxWidth="md">
      <Modal.Body className="p-4 product-details-dialog-content">
        <IconButton aria-label="close" onClick={props.onClose} className="close-button">
          <CloseIcon />
        </IconButton>
        <img  id={`product-detail-logo-img-${props.id}`} name="product-detail-logo-img" className={`${classes.carrierLogo} carrier-logo-lg`} src={props.quoteCardProps.logoUrl} alt={`${props.quoteCardProps.carrierName} Logo`} />

        <p className={`mt-3 textRating ${classes.primaryColor}`} id={`product-detail-rating-${props.id}`} name="product-detail-rating">
          {props.quoteCardProps.rating}
          <span className="ml-1 textRating" id={`product-detail-rating-text-${props.id}`} name="product-detail-rating-text">Rating</span>
        </p>

        <div className="product mb-1 d-flex" id={`product-detail-product-name-${props.id}`} name="product-detail-product-name">
          <span><strong>{props.quoteCardProps.productName}</strong></span>
        </div>

        <Row id={`product-detail-stats-${props.id}`} name="product-detail-stats">
          <Col xs="3" md="9" id={`product-detail-stats-info-${props.id}`} name="product-detail-stats-info">
            <Stats
              isAcceleratedUnderwriting={props.quoteCardProps.isAcceleratedUnderwriting}
              underwritingType={props.quoteCardProps.underwritingType}
              underwritingTimeTypical={props.quoteCardProps.underwritingTimeTypical}
              additionalOptionsStars={props.quoteCardProps.additionalOptionsStars}
              requiresMedicalExam={props.quoteCardProps.requiresMedicalExam}
              waverOfPremium={props.quoteCardProps.waverOfPremium}
              accidentalDeathBenefit={props.quoteCardProps.accidentalDeathBenefit}
              childRider={props.quoteCardProps.childRider}
              rate={props.quoteCardProps.premiumAs === 'monthly' ? props.quoteCardProps.monthlyRate : props.quoteCardProps.annualRate}
              monthlyRate={props.quoteCardProps.monthlyRate}
              annualRate={ props.quoteCardProps.annualRate}
              isMonthlyRate={props.quoteCardProps.premiumAs === 'monthly' ? true : false}
              rating={props.quoteCardProps.rating}
              isHighlighted={true}
              isProductDialog={true}
              riderCost={props.quoteCardProps.premiumAs === 'monthly' ? props.quoteCardProps.riderCostMonthly : props.quoteCardProps.riderCost}
              id={props.id}
            />
          </Col>

          <Col xs="6" md="3" id={`product-detail-select-content-${props.id}`} name="product-detail-select-content">
            <Button id={`select-btn-${props.id}`} name="select-btn" className="w-100 apply" color="primary" variant="contained" onClick={()=> handleSelectedProduct()} disabled={props.isProspectMode}>
              Select &nbsp;{props.appointmentIcon}
            </Button>
          </Col>
        </Row>

        <div className="product-details-info" id={`product-detail-info-${props.id}`} name="product-detail-info">{props.productDetails}</div>

        <div className="additional-options mt-4">
          <strong>
            <b id="additional-options-label" name="additional-options-label">Additional Options</b>
          </strong>
          <Row id={`product-detail-convertibility-${props.id}`} name="product-detail-convertibility">
            <Col md="3" className="mt-2 ml-2" id={`product-detail-convertibility-info-${props.id}`} name="product-detail-convertibility-info">
              <p className="option-title " id="product-convertibility-label" name="product-convertibility-label">
                <b>Convertibility</b>
              </p>
              {props.quoteCardProps.hasPermConversion ? (
                <div className='d-flex' id={`product-detail-convertible-content-${props.id}`} name="product-detail-convertible-content">
                  <SvgIcon className={`mr-1 ${classes.convertIcon}`} color='primary' fontSize='small' viewBox="0 0 9 6" iconName='tick' id={`product-detail-convertible-logo-${props.id}`} name="product-detail-convertible-logo"/>
                  <p className={`price-free ${classes.primaryColor}`} id={`product-convertible-text-${props.id}`} name="product-convertible-text">
                    <b>This product is convertible.</b>
                  </p>
                </div>
              ) : (
                  <div className='d-flex' id={`product-detail-not-convertible-content-${props.id}`} name="product-detail-not-convertible-content">
                    <SvgIcon className="mr-1" iconName="notConvertible" id={`product-detail-not-convertible-logo-${props.id}`} name="product-detail-not-convertible-logo"/>
                    <p className="price-red"  id={`product-not-convertible-text-${props.id}`} name="product-not-convertible-text">
                      <b>This product is not convertible.</b>
                    </p>
                  </div>
                )}
            </Col>
            {props.quoteCardProps.hasPermConversion && (
              <Col className="mt-2 ml-2" id={`product-detail-convertibility-content-${props.id}`} name="product-detail-convertibility-content">
                <ul id={`product-convertibility-${props.id}`} name="product-convertibility" className="grey-text" dangerouslySetInnerHTML={{ __html: props.quoteCardProps.convertibility }} />
              </Col>
            )}
          </Row>
          {(props.quoteCardProps.availableRiders && props.quoteCardProps.availableRiders.length > 0) && (
            <strong className="mt-4 ml-2">
              <b id="available-policy-riders-label" name="available-policy-riders-label">Available Policy Riders</b>
              <Tooltip title="Riders add benefits to or change the term of an insurance policy." className="toolInfo ml-2" id="available-policy-riders-tooltip" name="available-policy-riders-tooltip">
                <TooltipColor>
                  <InfoIcon color="action" id="available-policy-riders-tooltip-icon" name="available-policy-riders-tooltip-icon" />
                </TooltipColor>
              </Tooltip>
            </strong>
          )}

          {props.quoteCardProps.availableRiders &&
            props.quoteCardProps.availableRiders.length > 0 &&
            props.quoteCardProps.availableRiders.map((r, index) => {
              return (
                <Row key={index} id={`policy-riders-${props.id}`} name="policy-riders">
                  <Col md="3" id={`policy-riders-content-${props.id}`} name="policy-riders-content">
                    <p className="option-title mt-4 ml-3" id={`product-details-riders-${index}`} name="product-details-riders">{r.rider}</p>
                    {r.additionalCost && (
                      <p className="price-extra mt-2 ml-3" id={`product-additional-cost-content`} name={`product-additional-cost-content`}>
                        <MonetizationOnOutlinedIcon id={`product-additional-cost-icon`} name={`product-additional-cost-icon`}/> <b id={`product-additional-cost`} name={`product-additional-cost`} className="addtional">Additional Cost</b>
                      </p>
                    )}
                    {!r.additionalCost && (
                      <div className='d-flex ml-3 mt-2' id={`product-no-additional-cost-content`} name={`product-no-additional-cost-content`}>
                        <MonetizationOnOutlinedIcon />
                        <p className='noAdditionalText' id={`product-no-additional-cost`} name={`product-no-additional-cost`} >
                          <b className={`price-free noAddtional ${classes.primaryColor}`}>No Additional Cost</b>
                        </p>
                      </div>
                    )}
                  </Col>
                  <Col id={`product-riders-description-info-${props.id}`} name={`product-riders-description-info`} >
                    <p id={`product-riders-description-${index}`} name="product-riders-description" dangerouslySetInnerHTML={{ __html: r.description }} className="grey-text mt-4 ml-2" />
                  </Col>
                </Row>
              );
            })}
        </div>

        {hasRating && (
          <div className="ratings" id={`product-insurance-info-${props.id}`} name={`product-insurance-info`}>
            <p id="product-insurance-carrier-ratings-label" name="product-insurance-carrier-ratings-label">
              <strong>Insurance Carrier Ratings</strong>
            </p>
            <div className="d-flex rating-cards pt-0" id={`product-insurance-content-${props.id}`} name={`product-Insurance-content`}>
              {props.quoteCardProps.ratingAMBest && (
                <ProductDetailsRatingCard id={props.id} logo="/logos/ratings/ambest.jpg" rater="AM Best" rating={props.quoteCardProps.ratingAMBest} />
              )}
              {props.quoteCardProps.ratingSP && <ProductDetailsRatingCard id={props.id} logo="/logos/ratings/sp.jpg" rater="S&amp;P Global" rating={props.quoteCardProps.ratingSP} />}
              {props.quoteCardProps.ratingMoodys && (
                <ProductDetailsRatingCard id={props.id} logo="/logos/ratings/moodys.jpg" rater="Moody's" rating={props.quoteCardProps.ratingMoodys} />
              )}
              {props.quoteCardProps.ratingFitch && <ProductDetailsRatingCard id={props.id} logo="/logos/ratings/fitch.jpg" rater="Fitch" rating={props.quoteCardProps.ratingFitch} />}
              {props.quoteCardProps.ratingComdex && (
                <ProductDetailsRatingCard id={props.id} logo="/logos/ratings/comdex.jpg" rater="Comdex" rating={props.quoteCardProps.ratingComdex} />
              )}
            </div>
          </div>
        )}
        {props.quoteCardProps.productName === 'HavenTerm' ? (
          <diV className="mt-4" id={`product-disclosure-content-${props.id}`} name={`product-disclosure-content`}>
            <h3 className=" text-center" id="product-disclosure-label" name="product-disclosure-label">Disclosure</h3>
            <p className="grey-text" id="product-disclosure-info" name="product-disclosure-info">{props.quoteCardProps.disclosure}</p>
          </diV>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

const Stats = (props) => {
  const classes = useStyles();
  const isOptionalRider = (props.waverOfPremium || props.accidentalDeathBenefit || props.childRider);
  const isChildRider = (props.riderCost && props.riderCost !== 0);

  return (
    <div className="product-details-stat w-100" id={`product-details-stats-${props.id}`} name="product-details-stats">
      {props.isHighlighted && (
        <div>
        <Row id={`product-details-stats-content-${props.id}`} name="product-details-stats-content">
          <Col id={`product-details-stats-col-${props.id}`} name="product-details-stats-col" className={`d-flex ${props.isProductDialog ? 'justify-content-start' : 'justify-content-center'}`}>
            <p id={`quote-amount-rate-${props.id}`} name="quote-amount-rate" className='mb-0'>
              <NumberFormat
                className={`quote-amount-dollars-highlighted ${classes.primaryColor}`}
                value={props.rate}
                displayType={'text'}
                id={`amount-dollars-${props.id}`}
                name='amount-dollars'
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
              />
              <span className={`quote-amount-mo ${classes.primaryColor}`} id={`amount-mo-${props.id}`} name='amount-type'>{props.isMonthlyRate ? ' /mo' : ' /year'}</span>

            </p>
          </Col>
          </Row>
          <Row id={`product-details-stats-sub-content-${props.id}`} name="product-details-stats-sub-content">
            <Col id={`product-details-sub-stats-col-${props.id}`} name="product-details-sub-stats-col" className={`d-flex ${props.isProductDialog ? 'justify-content-start' : 'justify-content-center'}`}>
              <p id={`quote-sub-amount-rate-${props.id}`} name="quote-sub-amount-rate">
                <NumberFormat
                  className={`quote-sub-amount-dollars-highlighted`}
                  value={props.isMonthlyRate ? props.annualRate : props.monthlyRate}
                  displayType={'text'}
                  id={`sub-amount-dollars-${props.id}`}
                  name='sub-amount-dollars'
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                />
                <span className={`quote-sub-amount-mo`} id={`sub-amount-type-${props.id}`} name='sub-amount-type'>{props.isMonthlyRate ? ' /year' : ' /mo'}</span>

              </p>
            </Col>
          </Row>
      </div>
      )}

      <Row id={`product-details-info-text-${props.id}`} name="product-details-info-text">
        <Col xs='auto' className={isOptionalRider ? isChildRider ? "detail-box" : "": "pr-2"} id={`product-typically-takes-${props.id}`} name="product-typically-takes">
          <p id='typically-takes-label' name="typically-takes-label" className={`stat-title mt-0 mb-1 ${classes.primaryColor}`}><b>Typically Takes</b></p>
          <p id={`typically-takes-${props.id}`} name="typically-takes" className='stat-text mb-0'>{props.underwritingTimeTypical}</p>
        </Col>

        <Col xs='auto' className={isOptionalRider ? isChildRider ? "detail-box" : "": "pr-2"} id={`product-medical-exam-${props.id}`} name="product-medical-exam">
          <p id='medical-exam-label' name="medical-exam-label" className={`stat-title mt-0 mb-1 ${classes.primaryColor}`}><b>Medical Exam</b></p>
          <p id={`medical-exam-${props.id}`} name="medical-exam" className='stat-text mb-0'>{props.requiresMedicalExam}</p>
        </Col>
        <Col xs='auto' className={props.isHighlighted ? "" : isOptionalRider ? isChildRider ? "detail-box" : "": "pr-2"} id={`product-underwriting-option-${props.id}`} name="product-underwriting-option">
          <p className={`stat-title mt-0 mb-1 ${classes.primaryColor}`}><b>Underwriting Options</b></p>
          <p className='stat-text mb-3'>
            {props.isAcceleratedUnderwriting ?
              (<div>
                {props.underwritingType !== 'AU' && <div className='text-underwriting'>Full Underwriting/</div>}
                <div>Accelerated  Underwriting</div>
              </div>)
              : <div>Full Underwriting</div>
            }
          </p>
        </Col>
        {isOptionalRider ?
        isChildRider ?
          <Col xs='auto' className="detail-box" id={`rider-cost-detail-${props.id}`} name="rider-cost-detail">
            <p className={`stat-title mt-0 mb-1 ${classes.primaryColor}`}  id={`rider-cost-label-${props.id}`} name="rider-cost-label"><b>{props.isHighlighted ? 'Rider Cost': 'Total Rider Cost'}</b></p>
            <p className="stat-text mb-0" id={`rider-cost-${props.id}`} name="rider-cost">
              <span className="rider-amount-text">$</span>
              <NumberFormat
                className="rider-amount-dollars"
                value={props.riderCost}
                displayType={'text'}
                id={`rider-amount-dollars-${props.id}`}
                name="rider-amount-dollars"
                thousandSeparator={true}
                decimalScale={2}
                fixedDecimalScale={true}
              />              
              {props.isMonthlyRate ? (
                <span className="rider-amount-text" id={`rider-mo-${props.id}`} name="rider-mo"> /mo</span>
              ): (
                <span className="rider-amount-text" id={`rider-yr-${props.id}`} name="rider-yr"> /yr</span>
              )}
            </p>
          </Col>
          : (
              <Col className='stat-div'>
                <p className="stat-title mb-0" />
              </Col>
          ) : null }
      </Row>
    </div>
  );
};

QuoteCard.propTypes = {
  carrierName: PropTypes.string.isRequired,
  monthlyRate: PropTypes.number.isRequired,
  onClick: PropTypes.func.isRequired,
  productDetails: PropTypes.object.isRequired,
};



// WEBPACK FOOTER //
// ./src/components/Product/TermLifeInsurance/SubComponent/QuoteCard.js